import {
  useGetPopularSearchByIdQuery,
  useUpdatePopularSearchMutation,
} from "@/app/features/popularSearch/popularSearchApiSlice";
import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(2).max(50).label("Name").trim(),
  tileHeader: Yup.string().max(40).label("Tile header").trim(),
  articleUrl: Yup.string()
    .trim()
    .required("Guide article URL is required")
    .test(
      "valid-article-url",
      "Enter a full URL or a path such as /guides/small-suvs",
      (value) => {
        if (!value) return false;
        if (value.startsWith("/")) return value.length > 1;
        try {
          const parsed = new URL(value);
          return parsed.protocol === "http:" || parsed.protocol === "https:";
        } catch {
          return false;
        }
      }
    )
    .label("Guide article URL"),
  image: Yup.mixed()
    .nullable(true)
    .test(
      "image-required",
      "Image is required",
      function (value) {
        const preview = this.parent.previewImage;
        const isFile = value && value.type;
        const hasPreview = typeof preview === "string" && preview.trim() !== "";
        return isFile || hasPreview;
      }
    )
    .test(
      "file-type-validation",
      "Only the following formats are accepted: .jpg, .jpeg, .png, .webp",
      function (value) {
        if (!value || typeof value === "string") return true;
        return ["image/png", "image/jpg", "image/jpeg", "image/webp"].includes(
          value.type
        );
      }
    )
    .label("Image"),
});

export default function ViewPopularSearchPage() {
  const params = useParams();
  const { data, isLoading, isError, error } = useGetPopularSearchByIdQuery(
    params.id
  );

  const navigate = useNavigate();

  const [updatePopularSearch, { isLoading: isUpdating }] =
    useUpdatePopularSearchMutation();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("name", inputs.name);
      formData.append("tileHeader", inputs.tileHeader || "Buying guide");
      formData.append("articleUrl", inputs.articleUrl || "");
      formData.append("type", inputs.type || "Category");

      if (inputs.image) {
        formData.append("image", inputs.image);
      } else if (inputs.previewImage) {
        formData.append("image", inputs.previewImage);
      }

      const result = await updatePopularSearch({
        id: params.id,
        data: formData,
      });

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/popular-search");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const popularSearch = data?.data?.data;

  if (isLoading) {
    return (
      <Skeleton
        sx={{
          transform: "scale(1, 1.0)",
          transformOrigin: "0% 0%",
        }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError) {
    return <p>{error?.data?.message || "Something went wrong"}</p>;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Edit Buying Guide</Typography>
          </Stack>

          <Formik
            initialValues={{
              name: popularSearch?.name || "",
              tileHeader: popularSearch?.tileHeader || "Buying guide",
              articleUrl: popularSearch?.articleUrl || "",
              type: popularSearch?.type || "Category",
              image: null,
              previewImage: popularSearch?.image || null,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              setFieldError,
            }) => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="e.g. Small SUVs"
                  />
                  <FormInput
                    label="Tile header"
                    name="tileHeader"
                    type="text"
                    placeholder="Buying guide"
                  />
                  <FormInput
                    label="Guide article URL"
                    name="articleUrl"
                    type="text"
                    placeholder="https://thebeep.com.au/guides/your-guide-slug"
                  />
                  <FormImageInput
                    name="image"
                    imageValues={values.previewImage}
                    onChangeSelectedImage={(event) => {
                      setFieldTouched(event.target.name);
                      setFieldValue(event.target.name, event.target.files[0]);
                    }}
                    onError={(message) => {
                      setFieldError("image", message);
                    }}
                    onCheckRes={(checkResolution) => {
                      setFieldValue("checkResolution", checkResolution);
                    }}
                    checkResolution={values.checkResolution}
                    label="Image"
                    imageSrcFolderPath="popular-search/"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
